import { isBlockedUser } from "../utils/blockedUsers";

type AccessConfig = {
  presetRole?: string;
  routeKeys?: string[];
  global?: Record<string, unknown>;
  stages?: Array<{ stageId?: number; permissions?: Record<string, unknown> }>;
  sources?: Array<{ sourceId?: number; permissions?: Record<string, unknown> }>;
};

export function getAccessConfig(role: any): AccessConfig | null {
  const cfg = role?.permissions?.[0]?.access_config;
  return cfg && typeof cfg === "object" ? (cfg as AccessConfig) : null;
}

/**
 * Walks an arbitrary (possibly nested) object and counts string-leaf
 * permission entries. Returns:
 *  - granted: leaves whose value is a non-empty string other than "deny"
 *  - total: every string leaf encountered
 *
 * The shape we walk over is the role's `access_config` (`global`, each
 * stage's `permissions`, each source's `permissions`). All leaf values
 * are CRUD strings: "allow" | "deny" | "view" | "own" | "" (or missing).
 */
function countPermissionLeaves(node: unknown): { granted: number; total: number } {
  if (node == null) return { granted: 0, total: 0 };

  if (typeof node === "string") {
    const isGranted = node !== "" && node !== "deny";
    return { granted: isGranted ? 1 : 0, total: 1 };
  }

  if (typeof node === "boolean") {
    return { granted: node ? 1 : 0, total: 1 };
  }

  if (Array.isArray(node)) {
    return node.reduce(
      (acc, item) => {
        const sub = countPermissionLeaves(item);
        return { granted: acc.granted + sub.granted, total: acc.total + sub.total };
      },
      { granted: 0, total: 0 },
    );
  }

  if (typeof node === "object") {
    return Object.values(node as Record<string, unknown>).reduce<{
      granted: number;
      total: number;
    }>(
      (acc, val) => {
        const sub = countPermissionLeaves(val);
        return {
          granted: acc.granted + sub.granted,
          total: acc.total + sub.total,
        };
      },
      { granted: 0, total: 0 },
    );
  }

  return { granted: 0, total: 0 };
}

/**
 * The ADMIN-privilege preset family. Single definition, because more than one
 * feature keys off it (role cards, lead-phone masking exemption) and two
 * copies would drift the moment a preset is renamed.
 */
export function isAdminPreset(cfg: AccessConfig | null): boolean {
  const preset = cfg?.presetRole?.toLowerCase();
  return preset === "admin" || preset === "owner" || preset === "super_admin";
}

/** Same rule, straight from a `presetRole` string. */
export function isAdminPresetName(preset: string | null | undefined): boolean {
  const p = preset?.toLowerCase();
  return p === "admin" || p === "owner" || p === "super_admin";
}

export type RoleCounts = {
  /** People who can actually work in this role — blocked users excluded. */
  userCount: number;
  /** How many blocked holders were left out of `userCount` (0 = none). */
  blockedCount: number;
  permissionsCount: number;
  permissionsTotal: number;
  modulesCount: number;
  modulesTotal: number;
};

/**
 * BLOCKED HOLDERS DON'T COUNT (user directive 2026-09-23: "remove blocked
 * users from this count").
 *
 * A blocked user cannot sign in and cannot be assigned, so counting them as a
 * holder overstates the team. User Management already hides them from its
 * default roster (`useUserListQueryFilters` pins `isBlocked=false`, with the
 * header's "Blocked Users" toggle as the only way to see them), so the Role
 * Management card was the last screen still counting them — a card read
 * "2 users" while the User Management tab listed 1 under that role.
 *
 * Filtered HERE, at the surface: the pipeline keeps returning blocked users
 * because Role / User management still has to SHOW them — that is where you
 * unblock someone. See `utils/blockedUsers.ts`.
 */
export function splitBlockedHolders(
  role: { users?: unknown } | null | undefined,
): { userCount: number; blockedCount: number } {
  const users = role?.users;
  if (!Array.isArray(users)) return { userCount: 0, blockedCount: 0 };
  const blockedCount = users.filter(isBlockedUser).length;
  return { userCount: users.length - blockedCount, blockedCount };
}

/**
 * Compute display counts for the role pipeline cards.
 *
 * Totals (denominators) are computed from the **union** of keys across
 * every role in the pipeline, so the "/total" stays consistent across
 * cards. Admin/owner preset roles are forced to count == total because
 * those roles bypass permission checks regardless of what's persisted
 * in their access_config.
 */
export function computeRoleCounts(roles: any[] | undefined | null): Map<number, RoleCounts> {
  const out = new Map<number, RoleCounts>();
  if (!Array.isArray(roles) || roles.length === 0) return out;

  let permissionsTotal = 0;
  const routeKeyUniverse = new Set<string>();

  for (const role of roles) {
    const cfg = getAccessConfig(role);
    if (!cfg) continue;
    const t = countPermissionLeaves({
      global: cfg.global,
      stages: cfg.stages,
      sources: cfg.sources,
    }).total;
    if (t > permissionsTotal) permissionsTotal = t;
    for (const k of cfg.routeKeys ?? []) routeKeyUniverse.add(k);
  }

  const modulesTotal = routeKeyUniverse.size;

  for (const role of roles) {
    const cfg = getAccessConfig(role);
    const { userCount, blockedCount } = splitBlockedHolders(role);

    if (!cfg) {
      out.set(role.id, {
        userCount,
        blockedCount,
        permissionsCount: 0,
        permissionsTotal,
        modulesCount: 0,
        modulesTotal,
      });
      continue;
    }

    const { granted } = countPermissionLeaves({
      global: cfg.global,
      stages: cfg.stages,
      sources: cfg.sources,
    });

    const modulesCount = Array.isArray(cfg.routeKeys) ? cfg.routeKeys.length : 0;

    if (isAdminPreset(cfg)) {
      out.set(role.id, {
        userCount,
        blockedCount,
        permissionsCount: permissionsTotal,
        permissionsTotal,
        modulesCount: modulesTotal,
        modulesTotal,
      });
      continue;
    }

    out.set(role.id, {
      userCount,
      blockedCount,
      permissionsCount: granted,
      permissionsTotal,
      modulesCount,
      modulesTotal,
    });
  }

  return out;
}

/** Build short avatar initials from a role name, e.g. "Super Admin" → "SA". */
export function buildRoleInitials(name: string | undefined | null): string {
  if (!name) return "?";
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}
